// P1 Phase C — architectural opening identity (doors, windows / vision
// panels, pass-throughs). docs/implementation/P1_DESIGN_RECORDS_AND_SCHEDULES.md.
//
// An opening IS its placed symbol: a TakeoffConditionsDrawn row with
// Style='count' whose type is tagged SyncsToCriteriaField door_count /
// window_count / pass_throughs (or whose Shape is a door/window shape). No
// second door table — the row's TakeoffConditionDrawnID is the machine
// identity and the new `Mark` column (db/37) is the human one:
//   suggested at placement as '<prefix>-<room number>-<n>' (D-101-1),
//   editable in the condition flyout, NEVER renumbered by the system —
//   inserting or deleting another door leaves every existing mark alone,
//   carried verbatim by sp_CreateRevision.
// From/to rooms: `RoomID` is the room the symbol was placed in ("from");
// the "to" room is the estimator's RoomBID when set, else derived from wall
// adjacency (the other room whose perimeter passes through the symbol).
import { getRoomEdges, projectOntoSegment, DEFAULT_TUNABLES } from "../utils/takeoffGeometry";

export const OPENING_KINDS = [
  { key: "door",         prefix: "D",  label: "Door",                 field: "door_count",   shapes: ["door", "double-door", "double-egress-door", "bifold", "sliding-door"] },
  { key: "window",       prefix: "W",  label: "Window / vision panel", field: "window_count", shapes: ["window", "window-2", "window-3"] },
  { key: "pass_through", prefix: "PT", label: "Pass-through",         field: "pass_throughs", shapes: ["pass-through"] },
];
export const OPENING_KIND_BY_KEY = Object.fromEntries(OPENING_KINDS.map(k => [k.key, k]));

// The opening kind of a drawable type / placed row, or null for anything that
// is not an opening (HEPAs, lights, markers, linear runs, area fills). The
// legacy-field tag wins; a door/window Shape is the fallback for an untagged
// type. A row that carries a Style must be a count symbol.
export function openingKindOf(entity) {
  if (!entity) return null;
  if (entity.Style != null && entity.Style !== "count") return null;
  const byField = OPENING_KINDS.find(k => k.field === entity.SyncsToCriteriaField);
  if (byField) return byField;
  return OPENING_KINDS.find(k => k.shapes.includes(entity.Shape)) || null;
}
export const isOpening = (row) => openingKindOf(row) != null;

// 'D-101-3' → { prefix: 'D', room: '101', seq: 3 }; anything else → null.
export function parseMark(mark) {
  const m = /^([A-Za-z]+)-(.+?)-(\d+)$/.exec(String(mark ?? "").trim());
  return m ? { prefix: m[1].toUpperCase(), room: m[2], seq: +m[3] } : null;
}

// Room key used inside a mark: the room's estimator-facing number, else its id.
export const markRoomKey = (room) => (room?.RoomNumber != null && String(room.RoomNumber).trim()) || (room ? `R${room.RoomID}` : "X");

// Next free mark for an opening of `kind` placed in `room`, given the
// revision's existing drawn rows: sequence = 1 + the highest sequence already
// used for that prefix + room (parsed from every row's mark, case-insensitive
// — an estimator-edited mark still counts), so a deleted D-101-2 is never
// reissued while D-101-3 exists, and nothing existing is ever renumbered.
export function suggestOpeningMark(kind, room, conditions = []) {
  const k = typeof kind === "string" ? OPENING_KIND_BY_KEY[kind] : kind;
  if (!k) return null;
  const roomKey = markRoomKey(room);
  let max = 0;
  for (const c of conditions) {
    const p = parseMark(c?.Mark);
    if (p && p.prefix === k.prefix && p.room.toLowerCase() === roomKey.toLowerCase()) max = Math.max(max, p.seq);
  }
  return `${k.prefix}-${roomKey}-${max + 1}`;
}

// Stable schedule order: marked rows in natural order ('D-101-2' < 'D-101-10'
// < 'D-102-1'), unmarked rows after them by DisplayOrder then id.
export function compareOpeningMarks(a, b) {
  const ma = String(a.Mark ?? "").trim(), mb = String(b.Mark ?? "").trim();
  if (ma && !mb) return -1;
  if (!ma && mb) return 1;
  if (ma && mb) { const c = ma.localeCompare(mb, "en", { numeric: true, sensitivity: "base" }); if (c !== 0) return c; }
  const d = (+a.DisplayOrder || 0) - (+b.DisplayOrder || 0);
  return d !== 0 ? d : (+a.TakeoffConditionDrawnID || 0) - (+b.TakeoffConditionDrawnID || 0);
}

const parseGeom = (row) => { try { return JSON.parse(row?.GeometryJson || "null"); } catch { return null; } };

// { from, to, toSource: 'override' | 'geometry' | null }. `from` is the row's
// RoomID room. `to` is the estimator's RoomBID room when set; otherwise the
// nearest OTHER room whose perimeter passes within SYMBOL_WALL_SNAP_FT of the
// symbol's point (the door sits in a wall shared with it); null for an
// exterior opening or an unplaced room.
export function openingRooms(row, rooms = [], tunables = DEFAULT_TUNABLES) {
  const from = rooms.find(r => r.RoomID === row.RoomID) || null;
  if (row.RoomBID != null) return { from, to: rooms.find(r => r.RoomID === row.RoomBID) || null, toSource: "override" };
  const g = parseGeom(row);
  if (!g || !Number.isFinite(+g.x) || !Number.isFinite(+g.y)) return { from, to: null, toSource: null };
  const pt = { x: +g.x, y: +g.y };
  const tol = tunables?.SYMBOL_WALL_SNAP_FT ?? DEFAULT_TUNABLES.SYMBOL_WALL_SNAP_FT;
  let best = null;
  for (const r of rooms) {
    if (r.RoomID === row.RoomID) continue;
    for (const e of getRoomEdges(r)) {
      const { dist } = projectOntoSegment(pt, e.p1, e.p2);
      if (dist <= tol && (!best || dist < best.dist)) best = { room: r, dist };
    }
  }
  return { from, to: best?.room || null, toSource: best ? "geometry" : null };
}

// Clear width / height of an opening: the instance's own value, else the type default.
export function openingWidthFt(row) {
  const g = parseGeom(row);
  const w = g?.widthFt != null ? +g.widthFt : (row.DefaultWidthFt != null ? +row.DefaultWidthFt : null);
  return Number.isFinite(w) && w > 0 ? w : null;
}
// The geometry JSON of a placed point symbol (door / window / marker) with its width set to `widthFt`, or null when
// nothing should be written: not a placed symbol (no x / y), a bad width, or the width is already that.
export function symbolGeometryWithWidth(row, widthFt) {
  const w = +widthFt;
  if (!(w > 0) || row?.Style !== "count") return null;
  const g = parseGeom(row);
  if (!g || !Number.isFinite(+g.x) || !Number.isFinite(+g.y) || +g.widthFt === w) return null;
  return JSON.stringify({ ...g, widthFt: w });
}
export function openingHeightFt(row) {
  const h = row.HeightFtOverride != null && row.HeightFtOverride !== "" ? +row.HeightFtOverride : (row.DefaultHeightFt != null ? +row.DefaultHeightFt : null);
  return Number.isFinite(h) && h > 0 ? h : null;
}

// ── Opening deductions (Takeoff Canvas 2.0 Track D-3) ──────────────────────
// An opening deducts its own width × height from the WALL-AREA element of
// each room whose wall it sits in — derived live from the opening's own row,
// never persisted as a copied deduction (compatibility audit §4–§5). Whether
// it deducts is a policy, not a rule: the row's DeductionPolicy (db/37 —
// NULL / 'inherit' | 'deduct' | 'do_not_deduct') over the wall element's own
// default (RoomElements.SettingsJson.deductOpenings, false until the
// estimator turns it on). "Door = deduct" is never hard-coded.
export const DEDUCTION_POLICY = Object.freeze({ INHERIT: "inherit", DEDUCT: "deduct", DO_NOT_DEDUCT: "do_not_deduct" });
export const DEDUCTION_POLICY_OPTIONS = [
  { value: "inherit",       label: "Use the wall element's default" },
  { value: "deduct",        label: "Deduct this opening from wall area" },
  { value: "do_not_deduct", label: "Do not deduct" },
];
export function resolveOpeningDeductionPolicy({ openingPolicy, elementDefault = false }) {
  if (openingPolicy === DEDUCTION_POLICY.DEDUCT) return true;
  if (openingPolicy === DEDUCTION_POLICY.DO_NOT_DEDUCT) return false;
  return Boolean(elementDefault);
}
// Clear width × height in SF, or null when either dimension is unknown —
// an unsized opening is reported, never guessed.
export function openingAreaSf(row) {
  const w = openingWidthFt(row), h = openingHeightFt(row);
  return w && h ? w * h : null;
}
// The openings that sit in one of THIS room's walls: placed in it (RoomID),
// opening into it (RoomBID), or — when the rooms list is given — derived from
// wall adjacency exactly as openingRooms() does for the "to" room.
export function openingsForRoom(room, conditions = [], rooms = [], tunables = DEFAULT_TUNABLES) {
  const id = room?.RoomID;
  if (id == null || !Array.isArray(conditions)) return [];
  return conditions.filter(c => isOpening(c) && (
    c.RoomID === id || c.RoomBID === id
    || (c.RoomBID == null && c.RoomID !== id && rooms?.length > 0 && openingRooms(c, rooms, tunables).to?.RoomID === id)));
}
